import React, { useCallback } from "react"

import { MaterialReactTable } from "material-react-table"
import { defaultTableProps } from "./constants"
import { getProviders, getRoles } from "./apiUtils"
import { generateColsProps } from "./utils"
import { columns } from "./TableConfigs"
import {
  forecastType,
  resourceBudgetType,
} from "../../../pages/ResourceBudget/types"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { getTokenLocal } from "../../../../helper/utils"
import {
  loadProviders,
  loadingProviders,
} from "../../../redux/slices/resourceBudgetSlice"
import {
  primaryTabs as pT,
  secondayTabs,
} from "../../../pages/ResourceBudget/constants"
import { addDirtyCellStyling } from "./TableConfigs"

type editableTableProps = {
  state: resourceBudgetType | forecastType
  loading: boolean
  appendHandler: any
  isSlider?: boolean
  onDeleteSuccess?: () => void
}

const EditableTable = ({
  state,
  loading,
  appendHandler,
  isSlider = false,
  onDeleteSuccess,
}: editableTableProps) => {
  const token = getTokenLocal()
  const dispatch = useAppDispatch()

  const { dataIndex } = state
  const defaultEditMode = dataIndex === pT.nonClinical
  const { pinningCol, additionalPinningCols } = columns[dataIndex]

  // Enhanced handler that saves to both current state and persistent state
  const enhancedAppendHandler = useCallback(
    (data: any, editMode?: boolean) => {
      appendHandler(data, editMode)
    },
    [appendHandler],
  )

  const { colsConfig: baseColsConfig, enhancedData } = generateColsProps({
    state,
    token,
    isSlider,
    onDeleteSuccess,
  })

  // Add dirty cell styling to columns, skip for new row when present
  const colsConfig = addDirtyCellStyling(
    baseColsConfig,
    state.editState.dirty,
    state.editState.newEntry,
  )

  // Flag to track if blur is caused by keyboard navigation
  const [isKeyboardNavigation, setIsKeyboardNavigation] = React.useState(false)
  
  // Ref to store table instance for focus restoration
  const tableInstanceRef = React.useRef<any>(null)
  
  // Track previous newEntry state to detect when save completes
  const prevNewEntryRef = React.useRef(state.editState.newEntry)

  // Utility function to handle keyboard navigation
  const handleKeyboardNavigation = (
    event: React.KeyboardEvent,
    cell: any,
    table: any,
  ) => {
    if (event.key === "Escape") {
      // Escape: Exit editing mode
      event.preventDefault()
      table.setEditingCell(null)
      return
    }

    if (event.key === "Tab") {
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the next cell
      const currentValue = (event.target as HTMLInputElement).value
      enhancedAppendHandler(
        {
          index: cell.row.index,
          value: currentValue,
          field: cell.column.id,
          original: cell.row.original,
        },
        defaultEditMode,
      )

      const currentRowIndex = cell.row.index
      const currentColumnIndex = cell.column.id

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = colsConfig.filter(
        (col: any) =>
          col.enableEditing !== false &&
          col.accessorKey !== pinningCol &&
          col.accessorKey !== additionalPinningCols,
      )

      // Find current column index in editable columns
      const currentEditableIndex = editableColumns.findIndex(
        (col: any) => col.accessorKey === currentColumnIndex,
      )

      // If current column is not in editable columns, find the first editable column
      if (currentEditableIndex === -1) {
        if (editableColumns.length > 0) {
          const firstColumn = editableColumns[0]
          // Find the cell in the table's internal structure
          const firstCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === firstColumn.accessorKey)
          if (firstCell) {
            table.setEditingCell(firstCell)
          }
        }
        return
      }

      if (event.shiftKey) {
        // Shift+Tab: Move to previous cell
        if (currentEditableIndex > 0) {
          // Move to previous column in same row
          const prevColumn = editableColumns[currentEditableIndex - 1]
          const prevCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === prevColumn.accessorKey)
          if (prevCell) {
            table.setEditingCell(prevCell)
          }
        } else if (currentRowIndex > 1) {
          // Move to last column of previous row
          const prevRowIndex = currentRowIndex - 1
          const lastColumn = editableColumns[editableColumns.length - 1]
          const prevCell = table
            .getRow(prevRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === lastColumn.accessorKey)
          if (prevCell) {
            table.setEditingCell(prevCell)
          }
        }
      } else {
        // Tab: Move to next cell
        if (currentEditableIndex < editableColumns.length - 1) {
          // Move to next column in same row
          const nextColumn = editableColumns[currentEditableIndex + 1]
          const nextCell = table
            .getRow(currentRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === nextColumn.accessorKey)
          if (nextCell) {
            table.setEditingCell(nextCell)
          }
        } else if (currentRowIndex < enhancedData.length - 1) {
          // Move to first column of next row
          const nextRowIndex = currentRowIndex + 1
          const firstColumn = editableColumns[0]
          const nextCell = table
            .getRow(nextRowIndex)
            ?.getAllCells()
            ?.find((c: any) => c.column.id === firstColumn.accessorKey)
          if (nextCell) {
            table.setEditingCell(nextCell)
          }
        }
      }
    } else if (event.key === "Enter" && !event.shiftKey) {
      // Enter: Move to first cell of next row
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the next row
      const currentValue = (event.target as HTMLInputElement).value
      enhancedAppendHandler(
        {
          index: cell.row.index,
          value: currentValue,
          field: cell.column.id,
          original: cell.row.original,
        },
        defaultEditMode,
      )

      const currentRowIndex = cell.row.index

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = colsConfig.filter(
        (col: any) =>
          col.enableEditing !== false &&
          col.accessorKey !== pinningCol &&
          col.accessorKey !== additionalPinningCols,
      )

      if (
        currentRowIndex < enhancedData.length - 1 &&
        editableColumns.length > 0
      ) {
        // Move to first column of next row
        const nextRowIndex = currentRowIndex + 1
        const firstColumn = editableColumns[0]
        const nextCell = table
          .getRow(nextRowIndex)
          ?.getAllCells()
          ?.find((c: any) => c.column.id === firstColumn.accessorKey)
        if (nextCell) {
          table.setEditingCell(nextCell)
        }
      }
    } else if (event.key === "Enter" && event.shiftKey) {
      // Shift+Enter: Move to first cell of previous row
      event.preventDefault()

      // Set flag to indicate keyboard navigation
      setIsKeyboardNavigation(true)

      // Save the current cell value before moving to the previous row
      const currentValue = (event.target as HTMLInputElement).value
      enhancedAppendHandler(
        {
          index: cell.row.index,
          value: currentValue,
          field: cell.column.id,
          original: cell.row.original,
        },
        defaultEditMode,
      )

      const currentRowIndex = cell.row.index

      // Get all editable columns for the current row (excluding pinned columns)
      const editableColumns = colsConfig.filter(
        (col: any) =>
          col.enableEditing !== false &&
          col.accessorKey !== pinningCol &&
          col.accessorKey !== additionalPinningCols,
      )

      if (currentRowIndex > 1 && editableColumns.length > 0) {
        // Move to first column of previous row
        const prevRowIndex = currentRowIndex - 1
        const firstColumn = editableColumns[0]
        const prevCell = table
          .getRow(prevRowIndex)
          ?.getAllCells()
          ?.find((c: any) => c.column.id === firstColumn.accessorKey)
        if (prevCell) {
          table.setEditingCell(prevCell)
        }
      }
    }
  }

  // Restore focus to "no_of_days" cell after saving a new entry (non-clinical only)
  React.useEffect(() => {
    const wasNewEntry = prevNewEntryRef.current
    const isNewEntry = state.editState.newEntry
    
    // If newEntry changed from true to false, it means a save just completed
    if (wasNewEntry && !isNewEntry && defaultEditMode && tableInstanceRef.current) {
      // Use setTimeout to wait for table re-render after state update
      const timeoutId = setTimeout(() => {
        const table = tableInstanceRef.current
        if (!table) return
        
        try {
          // Find row at index 1 (the row that was just saved)
          const row = table.getRow(1)
          if (!row) return
          
          // Find the "no_of_days" cell in that row
          const cells = row.getAllCells()
          const noOfDaysCell = cells.find((cell: any) => cell.column.id === "no_of_days")
          
          if (noOfDaysCell) {
            // Set focus to the "no_of_days" cell
            table.setEditingCell(noOfDaysCell)
          }
        } catch (error) {
          console.warn("Failed to restore focus after save:", error)
        }
      }, 100) // Small delay to ensure table has re-rendered
      
      return () => clearTimeout(timeoutId)
    }
    
    // Update previous state
    prevNewEntryRef.current = isNewEntry
  }, [state.editState.newEntry, defaultEditMode])

  // Force re-render when data changes by including data length and IDs in key
  const tableKey = React.useMemo(() => {
    const dataHash = state.data?.length 
      ? state.data.map((d: any) => d?.id).join('-')
      : 'empty'
    return `editable-table-${dataIndex}-${state.data?.length || 0}-${dataHash}`
  }, [dataIndex, state.data?.length, state.data])

  return (
    <MaterialReactTable
      key={tableKey}
      columns={colsConfig}
      data={enhancedData}
      {...defaultTableProps}
      enableEditing={(row) => row.index > 0}
      editDisplayMode="cell"
      initialState={{
        density: "compact",
      }}
      muiTableBodyCellProps={({ table, cell }) => {
        // Store table instance for focus restoration (update on each render to ensure it's current)
        tableInstanceRef.current = table
        return {
          onClick: () => {
            table.setEditingCell(cell)
          },
          sx: {
            height: "60px",
            border: "0.5px solid #E4E4E4",
            boxShadow: "#00000022 1.95px  0px 2.6px",
            color: "black",
          },
        }
      }}
      muiEditTextFieldProps={({ cell, table }) => ({
        size: "small",
        autoFocus: true,
        onKeyDown: (event) => {
          handleKeyboardNavigation(event, cell, table)
        },
        onFocus: (event) => {
          // Select all text when cell is focused for editing
          event.target.select()
        },
        onBlur: (event) => {
          // Only append if not caused by keyboard navigation
          if (!isKeyboardNavigation) {
            enhancedAppendHandler(
              {
                index: cell.row.index,
                value: event.target.value,
                field: cell.column.id,
                original: cell.row.original,
              },
              defaultEditMode,
            )
          }
          // Reset flag after handling blur
          setIsKeyboardNavigation(false)
        },
      })}
      state={{
        isLoading: loading,
        columnPinning: {
          left: [pinningCol, additionalPinningCols],
        },
      }}
    />
  )
}

export default EditableTable
